<script lang="ts" module>
  /** The drag data type of a spell picked up from the bar or the spellbook. */
  export const SPELL_DRAG = 'application/x-arena-spell';
</script>

<script lang="ts">
  import { CLASS_SLOTS, isAbilityId, type AbilityId } from '@arena/shared';
  import type { ActionView } from '../../game/hud.svelte';

  let {
    actions,
    locked,
    names,
    size = 'normal',
    oncast,
    onhover,
    onplace,
  }: {
    actions: ActionView[];
    locked: boolean;
    names: boolean;
    /** Small for the other class's row, medium for Swap Class. */
    size?: 'small' | 'medium' | 'normal';
    oncast?: (id: AbilityId) => void;
    onhover: (id: AbilityId | null) => void;
    onplace: (id: AbilityId, slot: number) => void;
  } = $props();
</script>

<div
  class="bar"
  class:small={size === 'small'}
  class:medium={size === 'medium'}
  data-quiet
>
  {#each actions as action, i (action.abilityId)}
    {@const sweep =
      action.cooldownDuration > 0
        ? (action.cooldown / action.cooldownDuration) * 360
        : 0}
    <button
      class="slot"
      aria-label={action.name}
      class:utility={i === CLASS_SLOTS}
      class:no-resource={action.noResource}
      class:spent={action.uses === 0}
      class:out-of-range={action.outOfRange}
      class:unusable={action.unusable}
      class:queued={action.queued}
      class:active={action.active}
      class:unlocked={!locked}
      class:inert={!oncast}
      draggable={!locked}
      onclick={() => oncast?.(action.abilityId)}
      onmouseenter={() => onhover(action.abilityId)}
      onmouseleave={() => onhover(null)}
      ondragstart={(e) => e.dataTransfer?.setData(SPELL_DRAG, action.abilityId)}
      ondragover={(e) => {
        if (e.dataTransfer?.types.includes(SPELL_DRAG)) e.preventDefault();
      }}
      ondrop={(e) => {
        e.preventDefault();
        const id = e.dataTransfer?.getData(SPELL_DRAG) ?? '';
        if (isAbilityId(id)) onplace(id, i);
      }}
    >
      <img
        class="icon"
        src="/icons/{action.icon}.webp"
        alt=""
        draggable="false"
      />
      {#if names && size !== 'small'}<span class="label">{action.name}</span
        >{/if}
      {#if action.cooldown > 0}
        <span
          class="sweep"
          style="background: conic-gradient(rgba(0,0,0,0.7) {sweep}deg, transparent 0)"
        ></span>
        {#if action.cooldown > 1.6}<span class="cd"
            >{Math.ceil(action.cooldown)}</span
          >{/if}
      {/if}
      {#if action.outOfRange}<span class="range"></span>{/if}
      <span class="key">{action.key}</span>
      {#if action.uses !== null}<span class="uses">{action.uses}</span>{/if}
    </button>
  {/each}
</div>

<style>
  .bar {
    display: flex;
    gap: 6px;
    pointer-events: auto;
  }
  .slot {
    position: relative;
    width: 84px;
    height: 84px;
    padding: 0;
    border: 3px solid #6b5a35;
    border-radius: 6px;
    background: radial-gradient(circle at 30% 30%, #4a3a20, #1a140a);
    overflow: hidden;
    transition: border-color 0.15s;
  }
  .icon {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    transition: filter 0.15s;
  }
  .key {
    position: absolute;
    top: 2px;
    right: 6px;
    font-size: 16px;
    font-weight: bold;
    color: #eee;
    text-shadow:
      0 0 3px black,
      0 0 3px black;
  }
  .uses {
    position: absolute;
    bottom: 2px;
    right: 6px;
    font-size: 18px;
    font-weight: bold;
    color: #fff;
    text-shadow:
      0 0 3px black,
      0 0 3px black;
  }
  .utility {
    margin-left: 18px;
  }
  .spent .icon {
    filter: grayscale(1) brightness(0.5);
  }
  .label {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    padding: 8px 3px 3px;
    overflow: hidden;
    font-size: 11px;
    line-height: 1.15;
    text-align: center;
    text-wrap: balance;
    overflow-wrap: anywhere;
    color: #eee;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8) 40%);
    text-shadow: 0 0 3px black;
  }
  .sweep,
  .range {
    position: absolute;
    inset: 0;
  }
  .cd {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    font-size: 30px;
    font-weight: bold;
    color: white;
    text-shadow:
      0 0 4px black,
      0 0 4px black;
  }
  .no-resource .icon {
    filter: sepia(1) hue-rotate(180deg) brightness(0.6);
  }
  .out-of-range {
    border-color: #c8281e;
  }
  .out-of-range .icon {
    filter: grayscale(0.7) brightness(0.55);
  }
  .unusable .icon {
    filter: grayscale(1) brightness(0.4);
  }
  .range {
    background: radial-gradient(
      circle,
      transparent 35%,
      rgba(255, 40, 25, 0.55) 100%
    );
    box-shadow: inset 0 0 12px rgba(255, 50, 30, 0.8);
    animation: range-pulse 1.2s ease-in-out infinite alternate;
  }
  .out-of-range .key {
    color: #ff4a3a;
  }
  .queued {
    border-color: #ffd34a;
    box-shadow: 0 0 14px #ffd34a;
  }
  .active {
    border-color: #fff;
    box-shadow:
      0 0 14px #b48cff,
      inset 0 0 18px #b48cff;
  }
  .active .icon {
    filter: brightness(1.3) saturate(1.3);
  }
  .unlocked {
    cursor: grab;
    border-style: dashed;
  }
  .inert:not(.unlocked) {
    cursor: default;
  }
  .small {
    gap: 4px;
  }
  .small .slot {
    width: 44px;
    height: 44px;
    border-width: 2px;
    border-radius: 4px;
  }
  .small .key {
    top: 0;
    right: 3px;
    font-size: 11px;
  }
  .small .cd {
    font-size: 18px;
  }
  .medium .slot {
    width: 63px;
    height: 63px;
  }
  .medium .key {
    font-size: 14px;
  }
  .medium .cd {
    font-size: 24px;
  }

  @keyframes range-pulse {
    from {
      opacity: 0.55;
    }
    to {
      opacity: 1;
    }
  }
</style>
